import { useRef, useEffect } from "react";

/**
 * @callback ResponseParser
 *   A function that validates and parses a HTTP response.
 *
 * @param {Response} response
 *   The raw HTTP response from a fetch call.
 *
 * @returns {Promise}
 *   A promise for parsed data. Failure are handled with exceptions.
 */

/**
 * A frozen collection of standardized response‑parsing functions.
 *
 * Each parser:
 *   - Validates that the HTTP response has an OK status
 *   - Throws an Error for non‑OK responses
 *   - Returns the parsed response body in a specific format
 *
 * Available parsers:
 *
 * • ResponseParser.JSON
 *     Parses the response body as JSON and returns the resulting object.
 *
 * • ResponseParser.TEXT
 *     Parses the response body as plain text and returns a string.
 *
 * Usage:
 *     const data = await ResponseParser.JSON(response);
 *
 * Notes:
 *   - All parser functions are async.
 *   - The object is frozen to prevent accidental modification.
 */
export const ResponseParsers = Object.freeze({
  JSON: async (response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} (${response.statusText})`);
    return response.json();
  },

  TEXT: async (response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} (${response.statusText})`);
    return response.text();
  },
});

/**
 * A custom hook that repeatedly fetches data from a REST API endpoint using
 * a recursive setTimeout to prevent race conditions.
 *
 * @param {ResponseParser} parser
 *   Async function that validates and parses responses from the fetch calls.
 *   See prepared parsers in the ResponseParsers collection.
 *
 * @param {Function} handler
 *   Function called after each successful fetch attempt with any succesfully parsed data.
 *
 * @param {string} endpoint
 *   The URL of the REST API endpoint to fetch.
 *
 * @param {number} interval
 *   The milliseconds between the completion of one fetch and the start of the next.
 *
 * @param {number} [delay]
 *   Optional number of milliseconds to wait before doing the first polling.
 */
export function useRestPolling({parser, handler, endpoint, interval, delay = 0}) {

  const handler_ = useRef(handler);
  const parser_  = useRef(parser);
  const stopFlag = useRef(false);

  const stopPolling = () => {
    stopFlag.current = true;
  };

  handler_.current = handler;
  parser_.current = parser;

  useEffect(() => {
    if (!endpoint) return;
    stopFlag.current = false;

    let executeTimer = null;
    let abortController = new AbortController();

    const execute = async () => {
      if (stopFlag.current) return;

      try {
        const response = await fetch(endpoint, { signal: abortController.signal });
        const data     = await parser_.current(response);
        handler_.current(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error.message);
        }
      } finally {
        if (!abortController.signal.aborted) {
          executeTimer = setTimeout(execute, interval);
        }
      }
    };

    const startTimer = setTimeout(execute, delay);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(executeTimer);
      abortController.abort();
    };
  }, [endpoint, interval, delay]);

  return stopPolling;
}

